import { ReactElement, ChangeEvent, useState, useEffect } from "react";
import {
  RiverTextInput,
  RiverDialog,
  IValidationErrors,
  useValidation,
  useNotification,
  RiverFormSelect,
  RiverSpinner,
  PrimaryButton,
} from "@river/common-ui";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  ICustomer,
  LicenseDto,
  ILicense,
  ISite,
  SiteLicenseDto,
  ISiteLicense,
} from "@river/interfaces";
import { customerService } from "../../services";
import { Constants } from "@river/constants";
import styles from "./add-license-dialog.module.scss";
import { Box, IconButton } from "@mui/material";

interface IAddLicenseDialogProps {
  open: boolean;
  customer: ICustomer;
  licenseToEdit?: ILicense;
  onClose: (success: boolean) => void;
}

export const AddLicenseDialog = ({
  open,
  customer,
  licenseToEdit,
  onClose,
}: IAddLicenseDialogProps): ReactElement => {
  const [license, setLicense] = useState<LicenseDto>(new LicenseDto());
  const [sites, setSites] = useState<ISite[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({
    fields: {},
    list: [],
  });
  const notify = useNotification();
  const { validateFields, validateProperty } = useValidation();

  const resetValidationErrors = (): void =>
    setValidationErrors({ fields: {}, list: [] });

  const resetDialogState = (): void => {
    resetValidationErrors();
    setLicense(new LicenseDto());
  };

  const closeDialog = (success: boolean): void => {
    resetDialogState();
    onClose(success);
  };

  const loadSites = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const result = await customerService.getSites(customer!._id);
      setSites(result);
    } catch (message) {
      notify.error({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const onPropertyChange = async (
    event: ChangeEvent<{ name?: string; value: unknown }>
  ): Promise<void> => {
    let newState: LicenseDto = {
      ...license,
      [event.target.name!]: event.target.value,
    };

    if (event.target.name! === "count") {
      newState = {
        ...license,
        [event.target.name!]: Number(event.target.value),
      };
    }

    Object.setPrototypeOf(newState, Object.getPrototypeOf(license));
    setLicense(newState);
    const newErrors: IValidationErrors = await validateProperty(
      newState,
      event.target.name!,
      validationErrors
    );
    setValidationErrors(newErrors);
  };

  const validateLicense = async (
    License: LicenseDto
  ): Promise<IValidationErrors> => {
    const errors = await validateFields(License);
    setValidationErrors(errors);
    return errors;
  };

  const checkSitesError = (errors: IValidationErrors) => {
    const sitesArrayUniqueErr = errors?.fields?.sites?.errors.find(
      (err) => err.constraint === "arrayUnique"
    );

    if (sitesArrayUniqueErr) {
      notify.error({ message: sitesArrayUniqueErr.message });
    }
  };

  const addLicense = async (): Promise<void> => {
    const errors: IValidationErrors = await validateLicense(license);
    if (errors.list.length > 0) {
      checkSitesError(errors);

      return;
    }

    try {
      if (license?._id) {
        await customerService.updateCustomerLicense(
          customer._id!,
          license?._id!,
          license
        );
      } else {
        await customerService.createCustomerLicense(customer._id!, license);
      }

      const success = true;
      closeDialog(success);
    } catch (message) {
      notify.error({ message });
    }
  };

  const renderlicenseField = (): ReactElement => (
    <RiverFormSelect
      items={Object.values(Constants.license_type).map((licenseType) => ({
        text: licenseType,
        value: licenseType,
      }))}
      value={license.license}
      onChangeEvent={(event) => {
        onPropertyChange(
          event as ChangeEvent<{ name?: string; value: unknown }>
        );
      }}
      id="license"
      label="License"
      fullWidth
      validationErrors={validationErrors?.fields["license"]}
    />
  );

  const onParamPropertyChange = async (
    index: number,
    event: ChangeEvent<{ name: string; value: unknown }>
  ): Promise<void> => {
    const options = license.sites ?? [];

    const updated = options.map((item, i) => {
      if (i !== index) return item;

      const siteDto = Object.assign(new SiteLicenseDto(), item);

      let value = event.target.value;
      if (event.target.name === "count") {
        value = Number(value);
      }

      // @ts-ignore
      siteDto[event.target.name] = value;
      return siteDto;
    });

    const newState = Object.assign(new LicenseDto(), license, {
      sites: updated,
    });

    setLicense(newState);
  };

  const renderDescriptionField = (): ReactElement => (
    <div className={styles.fieldContainer}>
      <RiverTextInput
        id="description"
        label="Description..."
        value={license.description || ""}
        onChangeEvent={(event) => onPropertyChange(event)}
        validationErrors={validationErrors?.fields["description"]}
        inputProps={{ multiline: true, minRows: 5 }}
        fullWidth
      />
      {validationErrors?.fields["description"]?.errors?.[0] && (
        <span className={styles.errorMessage}>
          {validationErrors.fields["description"].errors[0].message}
        </span>
      )}
    </div>
  );
  const renderCountField = (): ReactElement => (
    <div className={styles.fieldContainer}>
      <RiverTextInput
        id="count"
        label="Count"
        value={license.count}
        onChangeEvent={(event) => onPropertyChange(event)}
        validationErrors={validationErrors?.fields["count"]}
        fullWidth
        inputProps={{ type: "number" }}
      />
      {validationErrors?.fields["count"]?.errors?.[0] && (
        <span className={styles.errorMessage}>
          {validationErrors.fields["count"].errors[0].message}
        </span>
      )}
    </div>
  );

  const handleRemoveParam = (index: number): void => {
    const params: ISiteLicense[] = license?.sites || [];
    if (params.length) {
      const newParams: ISiteLicense[] = [...params];
      newParams.splice(index, 1);
      const newDto: LicenseDto = {
        ...license,
        sites: newParams,
      };
      Object.setPrototypeOf(newDto, Object.getPrototypeOf(license));
      setLicense(newDto);
    }
  };

  const handleAddSite = () => {
    const params: ISiteLicense[] = license?.sites || [];
    const newParams: ISiteLicense[] = [...params];
    newParams.push({ site: "", count: 0 });
    const newDto: LicenseDto = {
      ...license,
      sites: newParams,
    };
    Object.setPrototypeOf(newDto, Object.getPrototypeOf(license));
    setLicense(newDto);
  };

  const renderAddSites = (): ReactElement => (
    <PrimaryButton
      text="Add Site"
      onClick={handleAddSite}
      classes={{ root: styles.addParamButton }}
      variant="text"
      startIcon={<AddCircleIcon />}
    />
  );

  const renderLicenseSiteRow = (site: SiteLicenseDto, index: number) => {
    return (
      <Box key={index} className={styles.sites}>
        <RiverFormSelect
          items={sites.map((site) => ({
            text: site.site,
            value: site.site,
          }))}
          value={site?.site ?? ""}
          onChangeEvent={(event) => {
            onParamPropertyChange(index, event);
          }}
          id="site"
          label="Site"
          fullWidth
          className={styles.site}
        />
        <RiverTextInput
          label="Count"
          id="count"
          inputProps={{
            type: "number",
            //@ts-ignore
            "data-testid": `site-count-input-${index}`,
          }}
          onChangeEvent={(event) => onParamPropertyChange(index, event)}
          className={styles.count}
          value={site.count}
        />
        <IconButton
          data-testid={`remove-site-${index}`}
          classes={{ root: styles.removeParamButton }}
          onClick={() => {
            handleRemoveParam(index);
          }}
          size="large"
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </Box>
    );
  };

  useEffect(() => {
    if (licenseToEdit) {
      const dto = Object.assign(new LicenseDto(), licenseToEdit);

      dto.sites = licenseToEdit.sites?.map((site) =>
        Object.assign(new SiteLicenseDto(), site)
      );

      setLicense(dto);
    }
  }, [licenseToEdit]);

  useEffect(() => {
    loadSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RiverDialog
      titleProps={{
        title: `${licenseToEdit ? "Edit License" : "Add a New License"}`,
        icon: licenseToEdit ? EditIcon : AddIcon,
      }}
      open={open}
      onClose={() => {
        const success = false;
        closeDialog(success);
      }}
      actionButtonText={licenseToEdit ? "Save" : "Add"}
      showActionsDivider={false}
      onSubmit={addLicense}
    >
      {renderlicenseField()}
      {renderDescriptionField()}
      {renderCountField()}

      <RiverSpinner show={isLoading} />
      {!isLoading && (
        <Box className={styles.sitesContainer}>
          {renderAddSites()}
          <Box className={styles.list}>
            {license.sites?.map((site, index) =>
              renderLicenseSiteRow(site, index)
            )}
          </Box>
        </Box>
      )}
    </RiverDialog>
  );
};
